/** @file Reads rig animations: compiled timelines, the event timeline index and the keyed-channel mask. */
import type { Pose2DJson } from '@pose2d/format/types';
import { channelOffsets } from '../../contracts/sample';
import type { AnimationData, SkeletonData, TimelineData } from '../../skeletonData';
import { deformSize } from '../../pose/deformSize';
import { coreFail } from '../coreFail';
import type { Interner } from './createInterner';
import { markKeyed } from './markKeyed';
import { readTimeline } from './readTimeline';
import type { TimelineCounts } from './readTimeline';

/** Animations in rig order; `data` (bones, slots, ik, attachments, events already read) gives counts and layout. */
export function readAnimations(animations: Pose2DJson['animations'], data: SkeletonData, placeholders: Interner): AnimationData[] {
  const layout = channelOffsets(data);
  const counts: TimelineCounts = {
    bones: data.bones.length, slots: data.slots.length, ik: data.ik.length, events: data.events.length,
    deformSizes: data.attachments.map((att) => deformSize(att)),
  };
  return animations.map((anim, a): AnimationData => {
    const timelines: TimelineData[] = [];
    anim.timelines.forEach((tl, i) => {
      const path = `animations[${a}].timelines[${i}]`;
      const read = readTimeline(tl, counts, placeholders, path);
      if (read === null) return;
      if (read.k === 'event' && timelines.some((t) => t.k === 'event')) {
        coreFail('VALIDATION', 'an animation holds at most one event timeline', { path, hint: 'put every event key of the animation on one timeline' });
      }
      timelines.push(read);
    });
    const keyedMask = new Uint8Array(layout.end);
    for (const tl of timelines) markKeyed(keyedMask, tl, layout);
    return { name: anim.name, duration: anim.duration, timelines, eventTimeline: timelines.findIndex((tl) => tl.k === 'event'), keyedMask };
  });
}
